import React from "react";
import { useAppSelector, useAppDispatch } from "../../store/hooks.ts";
import {
	selectResults,
	selectSelectedItem,
	setHasSearched,
	setSearchResults,
	setSelectedItem,
} from "../../store/slices/searchSlice.ts";
import SelectedResult from "./selectedresult/SelectedResult.tsx";

import {
	Box,
	Typography,
	List,
	ListItem,
	ListItemText,
	Skeleton,
	ListItemIcon,
} from "@mui/material";

import PersonIcon from "@mui/icons-material/Person";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import ListAltIcon from "@mui/icons-material/ListAlt";
import {
	setCalendarDate,
	setCalendarView,
	setSelectedEntityType,
} from "../../store/slices/calendarSlice.ts";

import { setSelectedSession } from "../../store/slices/sessionSlice.ts";

import { setSelectedEvent } from "../../store/slices/eventSlice.ts";
import { useNavigate } from "react-router-dom";
import { calculateEndTime } from "../calendar/helper/eventRendering.tsx";
import DatabaseView from "./DatabaseView.tsx";

const SearchResults = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const results = useAppSelector(selectResults);
	const searching = useAppSelector((state) => state.search.searching);
	const hasSearched = useAppSelector((state) => state.search.hasSearched);
	const selectedItem = useAppSelector(selectSelectedItem);

	const handleItemClick = (item: any) => {
		const dateStr = item.date_of_service;
		const startTime = item.start || "08:00";
		const startDateTime = `${dateStr}T${startTime}:00`;

		if (item.type === "session") {
			const endDateTime = calculateEndTime(dateStr, startTime, null);

			dispatch(setSelectedSession(item));
			dispatch(
				setSelectedSession({
					title: item.hospital?.name || "Session",
					id: item.id,
					start: startDateTime,
					end: endDateTime,
					surgeon: {
						...item.surgeon,
						name: `${item.surgeon?.first_name || ""} ${
							item.surgeon?.last_name || ""
						}`,
					},
					hospital: item.hospital,
					events: item.events,
				})
			);

			dispatch(setCalendarDate(dateStr));
			dispatch(setCalendarView("dayGridMonth"));
			dispatch(setSelectedEntityType("session"));
			navigate("/calendar");
		} else if (item.type === "event") {
			const session = item.session;

			if (session) {
				const sessionStartTime = session.start || "08:00";
				const sessionStart = `${session.date_of_service}T${sessionStartTime}:00`;
				const sessionEnd = calculateEndTime(
					session.date_of_service,
					sessionStartTime,
					null
				);

				dispatch(
					setSelectedSession({
						title: session.hospital?.name || "Session",
						id: session.id,
						start: sessionStart,
						end: sessionEnd,
						surgeon: {
							...session.surgeon,
							name: `${session.surgeon?.first_name || ""} ${
								session.surgeon?.last_name || ""
							}`,
						},
						hospital: session.hospital,
						events: session.events,
					})
				);
			} else {
				console.warn("No session found in event item");
			}

			dispatch(setSelectedEvent(item));
			dispatch(setCalendarDate(dateStr));
			dispatch(setCalendarView("dayGridMonth"));
			dispatch(setSelectedEntityType("event"));
			navigate("/calendar");
		} else if (item.type === "patient") {
			dispatch(setSelectedItem(item));
		}
	};

	const renderSkeleton = () =>
		Array.from({ length: 5 }).map((_, index) => (
			<ListItem key={index} divider>
				<ListItemText
					primary={<Skeleton variant="text" width="60%" />}
					secondary={<Skeleton variant="text" width="40%" />}
				/>
			</ListItem>
		));

	const getIcon = (type: string) => {
		switch (type) {
			case "patient":
				return <PersonIcon />;
			case "event":
				return <MedicalServicesIcon />;
			case "session":
				return <ListAltIcon />;
			default:
				return null;
		}
	};

	return (
		<Box sx={{ maxWidth: 800, ml: 0, mt: 4, textAlign: "left" }}>
			{searching ? (
				<List>{renderSkeleton()}</List>
			) : selectedItem ? (
				<SelectedResult />
			) : results.length > 0 ? (
				<>
					<Typography
						variant="body2"
						onClick={() => dispatch(setSearchResults([]))}
						sx={{
							cursor: "pointer",
							mb: 2,
							color: "primary.main",
							"&:hover": {
								textDecoration: "underline",
							},
						}}
					>
						← Back to database
					</Typography>
					<Typography variant="h6" gutterBottom>
						Results
					</Typography>
					<List>
						{results.map((item) => (
							<ListItem
								key={item.id}
								divider
								onClick={() => handleItemClick(item)}
								sx={{ cursor: "pointer" }}
							>
								<ListItemIcon>
									{getIcon(item.type)}
								</ListItemIcon>
								<ListItemText
									primary={(() => {
										switch (item.type) {
											case "patient":
												return `${item.first_name} ${item.last_name}`;
											case "event":
												return item.procedure
													? `Procedure: ${item.procedure}`
													: `Event ID: ${item.id}`;
											case "session": {
												const surgeon =
													item.surgeon?.first_name &&
													item.surgeon?.last_name
														? `${item.surgeon.first_name} ${item.surgeon.last_name}`
														: "Unknown surgeon";
												return `Session with ${surgeon}`;
											}
											default:
												return `ID: ${item.id}`;
										}
									})()}
									secondary={(() => {
										switch (item.type) {
											case "patient":
												return item.email || "—";
											case "event": {
												const date =
													item.date_of_service
														? new Date(
																item.date_of_service
														  ).toLocaleDateString()
														: "No date";
												const patientName = item.patient
													? `${item.patient.first_name} ${item.patient.last_name}`
													: "Unknown patient";
												return `${date} — ${patientName}`;
											}
											case "session": {
												const date =
													item.date_of_service
														? new Date(
																item.date_of_service
														  ).toLocaleDateString()
														: "No date";
												const hospital =
													item.hospital?.name ??
													"Unknown hospital";
												return `${date} — ${hospital}`;
											}
											default:
												return "";
										}
									})()}
								/>
							</ListItem>
						))}
					</List>
				</>
			) : hasSearched ? (
				<>
					<Typography
						variant="body2"
						onClick={() => {
							dispatch(setSearchResults([]));
							dispatch(setHasSearched(false));
						}}
						sx={{
							cursor: "pointer",
							mb: 2,
							color: "primary.main",
							"&:hover": {
								textDecoration: "underline",
							},
						}}
					>
						← Back to database
					</Typography>
					<Typography variant="body2">No results found.</Typography>
				</>
			) : (
				<DatabaseView />
			)}
		</Box>
	);
};

export default SearchResults;
